/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

/**
 * Platform Detection Utility
 * Auto-detects DAS platform type from equipment data
 */

import { IRemoteEquipment } from "../../../store/api/devices/types";

/**
 * Detect DAS platform from equipment data
 * Uses equipment types and model names to determine platform
 * 
 * @param equipment Array of equipment data
 * @returns Platform identifier (e.g., "ma3000", "ma6200-g1", "ma6000-g2")
 */
export function detectPlatform(equipment: IRemoteEquipment[]): string {
  if (!equipment || equipment.length === 0) {
    return "ma3000"; // Default fallback
  }

  // Check equipment types
  const equipmentTypes = new Set<string>();
  const models = new Set<string>();

  equipment.forEach((device) => {
    if (device.equipmentType) {
      equipmentTypes.add(device.equipmentType.toUpperCase());
    }
    if (device.model) {
      models.add(device.model.toUpperCase());
    }
  });

  // Strategy 1: Check model names (most reliable)
  const modelsArray = Array.from(models);
  for (const model of modelsArray) {
    if (
      model.includes("MA6000-G2") ||  model.includes("MA6000G2") 
    ) {
      return "ma6000-g2";
    }
    if (model.includes("MA6200-G1") || model.includes("MA6200G1") ) {
      return "ma6200-g1";
    }
    if (model.includes("MA6200-G2") || model.includes("MA6200G2") ) {
      return "ma6200-g2";
    }
    if (model.includes("MA3000") || model.includes("MA-3000")) {
      return "ma3000";
    }
  }

  // Strategy 2: Check equipment types (MA6000-G2 specific)
  // MA6000-G2 topology is DCU root with RIU children.
  const ma6000g2Types = ["DCU", "RIU", "DEU", "DXRU", "DXRU10G"];
  const hasMA6000G2Types = ma6000g2Types.some((type) => equipmentTypes.has(type));

  if (hasMA6000G2Types) {
    return "ma6000-g2";
  }

  // Strategy 3: Check equipment types (MA6200-G1 specific)
  // MA6200-G1 uses: Primary A2, Secondary A2, N2-RU, M2-RU, E2-O
  const ma6200g1Types = ["PRIMARY A2", "SECONDARY A2", "N2-RU", "M2-RU", "E2-O"];
  const hasMA6200G1Types = ma6200g1Types.some((type) => equipmentTypes.has(type));
  
  if (hasMA6200G1Types) {
   return "ma6200-g1";
  }

  // Strategy 4: Check equipment types (MA3000 specific)
  // MA3000 uses: HCM, ACM, RIM, OIM, FMM, RAU, MRU, RXU, FCM, MXU
  const ma3000Types = ["HCM", "ACM", "RIM", "OIM", "FMM", "RAU", "MRU", "RXU", "FCM", "MXU"];
  const hasMA3000Types = ma3000Types.some((type) => equipmentTypes.has(type));
  
  if (hasMA3000Types) {
   return "ma3000";
  }

  // Strategy 5: Check equipment types (MA6200-G2 specific)
  // MA6200-G2 uses: A3, E3, RU3
  const ma6200g2Types = ["PRIMARY A3", "SECONDARY A3", "N3-RU", "M3-RU-H", "E3-O","M3-RU-L","X3-RU"];
  const hasMA6200G2Types = ma6200g2Types.some((type) => equipmentTypes.has(type));

  if (hasMA6200G2Types) {
    return "ma6200-g2";
  }

  // Strategy 6: Check objectKey format
  if (equipment.length > 0 && equipment[0].objectKey) {
    const objectKey = equipment[0].objectKey;
    const parts = objectKey.split(".");

    // Expected: {sn}.{devType}.{deviceSn}
    if (parts.length >= 3) {
      const devType = parts.slice(1, parts.length - 1).join(".").trim().toUpperCase();

      if (ma3000Types.includes(devType)) {
        return "ma3000";
      }

      if (ma6000g2Types.includes(devType)) {
        return "ma6000-g2";
      }

      if (ma6200g1Types.includes(devType)) {
        return "ma6200-g1";
      }

      if (ma6200g2Types.includes(devType)) {
        return "ma6200-g2";
      }
    }
  }

  // Default fallback
  console.warn("[detectPlatform] Could not detect platform, using default: ma3000");
  console.log("[detectPlatform] Equipment types:", Array.from(equipmentTypes));
  console.log("[detectPlatform] Models:", Array.from(models));
  return "ma3000";
}
